/**
 * Custom element for the blog posts list section.
 *
 * "Show More" reveals 3 deferred articles per click. Once all
 * server-rendered articles are visible, it fetches the next Shopify
 * page via the Section Rendering API and continues revealing 3 at a time.
 */
class BlogPostsList extends HTMLElement {
  #grid = null;
  #wrapper = null;
  #btn = null;
  #busy = false;

  connectedCallback() {
    this.#grid = this.querySelector('[data-blog-grid]');
    this.#wrapper = this.querySelector('[data-show-more-wrapper]');
    this.#btn = this.querySelector('[data-show-more-btn]');

    if (this.#btn) {
      this.#btn.addEventListener('click', () => this.#handleShowMore());
    }

    this.#syncButton();
  }

  /** @returns {Element[]} */
  #deferred() {
    return Array.from(this.#grid?.querySelectorAll('.blog-article--deferred') ?? []);
  }

  #handleShowMore() {
    if (this.#busy) return;

    const hidden = this.#deferred();

    if (hidden.length > 0) {
      hidden.slice(0, 3).forEach((el) => el.classList.remove('blog-article--deferred'));
      this.#syncButton();
    } else if (this.#wrapper?.dataset.hasNext === 'true') {
      this.#fetchNextPage();
    }
  }

  async #fetchNextPage() {
    const nextPage = this.#wrapper?.dataset.nextPage;
    const sectionId = this.getAttribute('section-id');
    if (!nextPage || !sectionId || !this.#grid) return;

    this.#busy = true;
    if (this.#btn) this.#btn.disabled = true;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    url.searchParams.set('sections', sectionId);
    url.hash = '';

    try {
      const res = await fetch(url.toString());
      const data = await res.json();
      const html = data[sectionId];
      if (!html) throw new Error('Empty section response');

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-blog-grid]');
      if (!newGrid) throw new Error('Grid not found in response');

      // Append fetched articles as deferred
      Array.from(newGrid.querySelectorAll('.blog-article')).forEach((item) => {
        item.classList.add('blog-article--deferred');
        this.#grid.appendChild(item);
      });

      // Update has-next state from fetched page metadata
      const fetched = parseInt(nextPage, 10);
      const last = parseInt(newGrid.dataset.lastPage ?? '1', 10);
      if (fetched < last) {
        this.#wrapper.dataset.hasNext = 'true';
        this.#wrapper.dataset.nextPage = String(fetched + 1);
      } else {
        this.#wrapper.dataset.hasNext = 'false';
        delete this.#wrapper.dataset.nextPage;
      }

      // Reveal first 3 of the new batch
      this.#deferred().slice(0, 3).forEach((el) => el.classList.remove('blog-article--deferred'));
    } catch (err) {
      console.error('[blog-posts-list] Failed to load more articles:', err);
    } finally {
      this.#busy = false;
      if (this.#btn) this.#btn.disabled = false;
      this.#syncButton();
    }
  }

  /** Hide the button when there is nothing left to show */
  #syncButton() {
    if (!this.#wrapper) return;
    const hasDeferred = this.#deferred().length > 0;
    const hasNextPage = this.#wrapper.dataset.hasNext === 'true';
    this.#wrapper.hidden = !hasDeferred && !hasNextPage;
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
